<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create Author'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"

            model="App\Models\Author"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <Select
            label="* Is author inactive?"
            name="is_inactive"
            v-model.number="form.is_inactive"
            class="mt-3 mb-3"
            :options="[
                {value: 0, name: 'No', default: true},
                {value: 1, name: 'Yes'},
            ]"
            :error="errors.is_inactive ? errors.is_inactive[0] : null"
        />

        <input-text
            v-if="form.is_inactive == 1"
            label="* Info box for an inactive account"
            name="inactive_info"
            v-model="form.inactive_info"
            class="mb-3"
            :maxlength="255"
            :error="errors.inactive_info ? errors.inactive_info[0] : null"
        />

        <base-page-fields
            :form="form"
            :errors="errors"
            :hide-href-langs="true"
            :editable-timestamps="true"
        ></base-page-fields>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Language</p>
            <reference
                create-text="Create new language and link language to this page"
                link-text="Link an existing language"
                :creatable="false"
                :deletable="false"
                :error="errors.language_id ? errors.language_id[0] : null"
                v-model="form.language"
                model-data="Site Language"
            />
        </div>

        <media-reference
            :language="form.language"
            v-model="form.profile_photo"
            class="mt-3"
            label="Profile Photo"
            :media-types="['Image']"
            name="profile_photo_id"
            :error="errors.profile_photo_id ? errors.profile_photo_id[0] : null"
        />

        <input-text
            label="Full Name"
            name="full_name"
            v-model="form.full_name"
            class="mt-2"
            :error="errors.full_name ? errors.full_name[0] : null"
        />

        <input-rich-text
            label="Long Bio"
            name="long_bio"
            v-model="form.long_bio"
            class="mt-2"
            :error="errors.long_bio ? errors.long_bio[0] : null"
            :maxlength="5000"
            :language="form.language"
        />

        <input-textarea
            label="Short Bio"
            name="short_bio"
            v-model="form.short_bio"
            class="mt-2"
            :error="errors.short_bio ? errors.short_bio[0] : null"
            :maxlength="500"
            :language="form.language"
        />

        <input-text
            label="Facebook URL"
            name="facebook_url"
            v-model="form.facebook_url"
            class="mt-2"
            :error="errors.facebook_url ? errors.facebook_url[0] : null"
            :maxlength="255"
        />
        <input-text
            label="Twitter URL"
            name="twitter_url"
            v-model="form.twitter_url"
            class="mt-2"
            :error="errors.twitter_url ? errors.twitter_url[0] : null"
            :maxlength="255"
        />
        <input-text
            label="LinkedIn URL"
            name="linkedin_url"
            v-model="form.linkedin_url"
            class="mt-2"
            :error="errors.linkedin_url ? errors.linkedin_url[0] : null"
            :maxlength="255"
        />
        <input-text
            label="Email"
            name="email"
            v-model="form.email"
            class="mt-2"
            :error="errors.email ? errors.email[0] : null"
            :maxlength="255"
        />

        <Select
            label="* Job Title?"
            name="job"
            v-model="form.job"
            class="mt-2"
            :options="[
                {value: null, name: 'Please select job', default: true},
                {value: 'casino_complaint_specialist', name: 'Casino Complaint Specialist'},
                {value: 'head_of_content', name: 'Head of Content'},
                {value: 'casino_analyst', name: 'Casino Analyst'},
                {value: 'casino_quality_specialist', name: 'Casino Quality Specialist'},
                {value: 'expert_content_writer', name: 'Expert Content Writer'},
                {value: 'other', name: 'Other'},
            ]"
            :error="errors.job ? errors.job[0] : null"
        />

        <input-text
            v-if="form.job == 'other'"
            label="* Other Job Title"
            name="job_other"
            v-model="form.job_other"
            class="mt-2"
            :error="errors.job_other ? errors.job_other[0] : null"
            :maxlength="255"
        />

        <input-date
            label="Start Date"
            name="start_date"
            v-model="form.start_date"
            class="mt-3"
            :error="errors.start_date ? errors.start_date[0] : null"
        />
        <input-rich-text
            label="My Passion"
            name="my_passion"
            v-model="form.my_passion"
            class="mt-2"
            :error="errors.my_passion ? errors.my_passion[0] : null"
            :language="form.language"
        />
        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Passion Categories</p>
            <multi-reference
                create-text="Create new passion category and link passion category to this page"
                link-text="Link existing passion categories"
                :linkable="false"
                v-model="form.passions"
                :language="form.language"
                model-data="Passion"
                :error="errors.passions ? errors.passions[0] : null"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="our_expert_anchor"
                label="Our Expert Heading Jump ID (excluding #)"
                v-model="form.our_expert_anchor"
                class="w-full mr-3"
                :error="errors.our_expert_anchor ? errors.our_expert_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="our_expert_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.our_expert_anchor_text"
                class="w-full"
                :error="errors.our_expert_anchor_text ? errors.our_expert_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="casino_reviews_anchor"
                label="Casino Reviews Heading Jump ID (excluding #)"
                v-model="form.casino_reviews_anchor"
                class="w-full mr-3"
                :error="errors.casino_reviews_anchor ? errors.casino_reviews_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="casino_reviews_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.casino_reviews_anchor_text"
                class="w-full"
                :error="errors.casino_reviews_anchor_text ? errors.casino_reviews_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="game_reviews_anchor"
                label="Game Reviews Heading Jump ID (excluding #)"
                v-model="form.game_reviews_anchor"
                class="w-full mr-3"
                :error="errors.game_reviews_anchor ? errors.game_reviews_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="game_reviews_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.game_reviews_anchor_text"
                class="w-full"
                :error="errors.game_reviews_anchor_text ? errors.game_reviews_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="slot_reviews_anchor"
                label="Slot Reviews Heading Jump ID (excluding #)"
                v-model="form.slot_reviews_anchor"
                class="w-full mr-3"
                :error="errors.slot_reviews_anchor ? errors.slot_reviews_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="slot_reviews_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.slot_reviews_anchor_text"
                class="w-full"
                :error="errors.slot_reviews_anchor_text ? errors.slot_reviews_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="articles_anchor"
                label="Articles Heading Jump ID (excluding #)"
                v-model="form.articles_anchor"
                class="w-full mr-3"
                :error="errors.articles_anchor ? errors.articles_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="articles_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.articles_anchor_text"
                class="w-full"
                :error="errors.articles_anchor_text ? errors.articles_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="blogs_anchor"
                label="Blogs Heading Jump ID (excluding #)"
                v-model="form.blogs_anchor"
                class="w-full mr-3"
                :error="errors.blogs_anchor ? errors.blogs_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="blogs_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.blogs_anchor_text"
                class="w-full"
                :error="errors.blogs_anchor_text ? errors.blogs_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="guides_anchor"
                label="Guides Heading Jump ID (excluding #)"
                v-model="form.guides_anchor"
                class="w-full mr-3"
                :error="errors.guides_anchor ? errors.guides_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="guides_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.guides_anchor_text"
                class="w-full"
                :error="errors.guides_anchor_text ? errors.guides_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="fact_checked_anchor"
                label="Fact Checked Heading Jump ID (excluding #)"
                v-model="form.fact_checked_anchor"
                class="w-full mr-3"
                :error="errors.fact_checked_anchor ? errors.fact_checked_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="fact_checked_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.fact_checked_anchor_text"
                class="w-full"
                :error="errors.fact_checked_anchor_text ? errors.fact_checked_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <div class="flex mt-2">
            <input-text
                name="my_passion_anchor"
                label="My Passion Heading Jump ID (excluding #)"
                v-model="form.my_passion_anchor"
                class="w-full mr-3"
                :error="errors.my_passion_anchor ? errors.my_passion_anchor[0] : null"
                :maxlength="255"
            />

            <input-text
                name="my_passion_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.my_passion_anchor_text"
                class="w-full"
                :error="errors.my_passion_anchor_text ? errors.my_passion_anchor_text[0] : null"
                :maxlength="255"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import MediaReference from "./media/MediaReference";
import Select from "./form/Select.vue";

export default {
    name: "AuthorForm",
    components: {Select, MediaReference },
    props:  {
        languageData: {
            type: Object,
            required: false,
        }
    },
    mixins: [crudForm, pageFormData],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                is_inactive: this.getFormData('is_inactive', 0),
                inactive_info: this.getFormData('inactive_info'),
                h1_anchor: this.getFormData("h1_anchor"),
                long_bio: this.getFormData("long_bio"),
                short_bio: this.getFormData("short_bio"),
                language_id: this.getFormData("language_id"),
                profile_photo_id: this.getFormData("profile_photo_id"),
                profile_photo: this.getFormData("profile_photo"),
                facebook_url: this.getFormData("facebook_url"),
                twitter_url: this.getFormData("twitter_url"),
                linkedin_url: this.getFormData("linkedin_url"),
                job: this.getFormData('job'),
                job_other: this.getFormData('job_other'),
                start_date: this.getFormData('start_date'),
                full_name: this.getFormData('full_name'),
                my_passion: this.getFormData('my_passion'),
                passions: this.getFormData('passions'),
                email: this.getFormData('email'),

                our_expert_anchor: this.getFormData('our_expert_anchor'),
                our_expert_anchor_text: this.getFormData('our_expert_anchor_text'),
                casino_reviews_anchor: this.getFormData('casino_reviews_anchor'),
                casino_reviews_anchor_text: this.getFormData('casino_reviews_anchor_text'),
                game_reviews_anchor: this.getFormData('game_reviews_anchor'),
                game_reviews_anchor_text: this.getFormData('game_reviews_anchor_text'),
                slot_reviews_anchor: this.getFormData('slot_reviews_anchor'),
                slot_reviews_anchor_text: this.getFormData('slot_reviews_anchor_text'),
                articles_anchor: this.getFormData('articles_anchor'),
                articles_anchor_text: this.getFormData('articles_anchor_text'),
                blogs_anchor: this.getFormData('blogs_anchor'),
                blogs_anchor_text: this.getFormData('blogs_anchor_text'),
                guides_anchor: this.getFormData('guides_anchor'),
                guides_anchor_text: this.getFormData('guides_anchor_text'),
                fact_checked_anchor: this.getFormData('fact_checked_anchor'),
                fact_checked_anchor_text: this.getFormData('fact_checked_anchor_text'),
                my_passion_anchor: this.getFormData('my_passion_anchor'),
                my_passion_anchor_text: this.getFormData('my_passion_anchor_text'),

                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
            },
        };
    },
    watch: {
        "form.profile_photo": function(newValue){
            if(newValue) {
                this.form.profile_photo_id = newValue.id;
            }
        },
        "form.language": function (newValue) {
            if(newValue) {
                this.form.language_id = newValue.id;
            }
        },
        "form.is_inactive": function (newValue) {
            if(newValue == 0) {
                this.form.inactive_info = null;
            }
        },
        "form.job": function (newValue) {
            if (newValue !== 'other') {
                this.form.job_other = null;
            }
        },
    },
    mounted() {
        if (this.languageData) {
            this.form.language_id = this.languageData.id;
        }
     },
};
</script>
